import React, { useImperativeHandle } from 'react';

import { motion, useInView, useReducedMotion } from 'framer-motion';
import PropTypes from 'prop-types';

import * as Styled from './DynamicRenderer.style';

/************************************************
 * AnimatedGroup – Animation logic for DynamicRenderer
 * ------------------------------------------------
 *  ‣ Contains all animation constants and variants
 *  ‣ Provides motion wrappers for groups and items
 *  ‣ Handles animation style selection logic
 ************************************************/

/* ===============TUNABLE CONSTANTS============== */
export const GROUP_DELAY_MULT = 0.1; // Reduced from 0.25 to 0.1
export const BASE_DURATION = 0.275; // Base animation duration for each element
export const MAX_ANIMATION_DELAY = 10.0; // Increased to allow up to 10 components with 1s delays each
export const ANIMATION_STYLE = 'fade';
/* =================================================== */

/* ---------------------------------------------
 * Styled → Motion wrappers
 * -------------------------------------------*/
export const MotionGroup = motion.create(Styled.DynamicRendererInstructionGroup);
export const MotionItem = motion.create(Styled.DynamicRendererComponentWrapper);

/* ---------------------------------------------
 * Animation presets
 * -------------------------------------------*/
// const baseTransition = { duration: BASE_DURATION, ease: [0.17, 0.67, 0.83, 0.67] };
const baseTransition = { duration: BASE_DURATION, ease: 'easeInOut' };

/**
 * Calculate delay with a maximum cap to prevent exponential slowdown
 * @param {number} index - Item index
 * @param {number} multiplier - Delay multiplier
 * @param {boolean} isNewComponent - Whether this is a new component
 * @returns {number} Capped delay value
 */
const getConstrainedDelay = (index, multiplier, isNewComponent = false) => {
  // For new components in POST mode, no group delay
  if (isNewComponent) {
    return 0;
  }
  // For existing items on page load, cap the delay
  const rawDelay = index * multiplier;
  return Math.min(rawDelay, MAX_ANIMATION_DELAY);
};

export const variants = {
  /**
   * Opacity only
   * @param {number} i - Index of the group
   * @param {boolean} isNewComponent - Whether this is a new component
   * @returns {object} Animation variant configuration
   */
  fade: (i, isNewComponent = false) => ({
    hidden: { opacity: 0.8 },
    visible: {
      opacity: 1,
      transition: {
        ...baseTransition,
        delay: getConstrainedDelay(i, GROUP_DELAY_MULT, isNewComponent),
        // Use consistent easeInOut instead of spring for predictable timing
        ease: 'easeInOut'
      }
    }
  }),
  /**
   * Slide up + fade
   * @param {number} i - Index of the group
   * @param {boolean} isNewComponent - Whether this is a new component
   * @returns {object} Animation variant configuration
   */
  slide: (i, isNewComponent = false) => ({
    hidden: { opacity: 0, y: -16, x: 16 },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        ...baseTransition,
        delay: getConstrainedDelay(i, GROUP_DELAY_MULT, isNewComponent)
      }
    }
  }),
  /**
   * Formerly blur → clear. Now simplified to opacity only for performance.
   * @param {number} i - Index of the group
   * @param {boolean} isNewComponent - Whether this is a new component
   * @returns {object} Animation variant configuration
   */
  blur: (i, isNewComponent = false) => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        ...baseTransition,
        delay: getConstrainedDelay(i, GROUP_DELAY_MULT, isNewComponent)
      }
    }
  }),
  /**
   * Grow / accordion: starts collapsed (scaleY 0) and
   * expands downward while fading in.
   * Using scaleY avoids animating `height: auto`.
   * @param {number} i - Index of the group
   * @param {boolean} isNewComponent - Whether this is a new component
   * @returns {object} Animation variant configuration
   */
  grow: (i, isNewComponent = false) => ({
    hidden: { opacity: 0, scaleY: 0, originY: 0 },
    visible: {
      opacity: 1,
      scaleY: 1,
      originY: 0,
      transition: {
        ...baseTransition,
        delay: getConstrainedDelay(i, GROUP_DELAY_MULT, isNewComponent)
      }
    }
  })
};

/* ---------------------------------------------
 * Child‑item variants (true sequential animation).
 * Uses staggerChildren to wait for actual completion
 * -------------------------------------------*/
export const childVariantsByStyle = {
  fade: {
    hidden: { opacity: 0, scale: 0.99, y: 2 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        ...baseTransition,
        // Slight delay to coordinate with parent completion
        delay: 0.05,
        ease: 'easeOut'
      }
    }
  },
  slide: {
    hidden: { opacity: 0, y: 12, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        ...baseTransition,
        // No individual delay - controlled by parent staggerChildren
        delay: 0
      }
    }
  },
  blur: {
    hidden: { opacity: 0, scale: 0.96 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        ...baseTransition,
        // No individual delay - controlled by parent staggerChildren
        delay: 0
      }
    }
  },
  grow: {
    hidden: { opacity: 0, scaleY: 0, originY: 0 },
    visible: {
      opacity: 1,
      scaleY: 1,
      originY: 0,
      transition: {
        ...baseTransition,
        // No individual delay - controlled by parent staggerChildren
        delay: 0
      }
    }
  }
};

/* ---------------------------------------------
 * Animation style selection logic
 * -------------------------------------------*/
// Fallback if caller passes an unknown style
export const getGroupVariant = (style) => variants[style] || variants.fade;
export const getChildVariant = (style) => childVariantsByStyle[style] || childVariantsByStyle.fade;

/* ---------------------------------------------
 * AnimatedGroup Component
 * -------------------------------------------*/
/**
 * AnimatedGroup - Wraps content with animation variants
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Content to animate
 * @param {number} props.index - Index for staggered animation
 * @param {string} props.animationStyle - Animation style ('fade', 'slide', 'blur', 'grow')
 * @param {string} props.currentMode - Current mode ('get' or 'post') for animation control
 * @param {boolean} props.isNewComponent - Whether this is a new component that should animate
 * @param {Function} props.onAnimationComplete - Callback when animation completes
 * @param {React.Ref} props.ref - Ref to attach to the motion element
 * @returns {React.ReactElement} The animated group wrapper
 */
const AnimatedGroup = React.forwardRef(
  (
    {
      children,
      index = 0,
      animationStyle = ANIMATION_STYLE,
      currentMode = 'get',
      isNewComponent = false,
      fullWidth = false,
      centerGreeting = false,
      onAnimationComplete = () => {}
    },
    ref
  ) => {
    // Timing tracking - initialize immediately to avoid race conditions
    const animationStartTime = React.useRef(null);

    // Create internal ref for useInView, but forward the external ref
    const internalRef = React.useRef(null);

    // Reduced motion preference
    const prefersReducedMotion = useReducedMotion();

    // Use useInView to trigger animation only when element enters viewport
    // For new components in POST mode, use viewport detection
    // For initial/GET mode, trigger immediately (once: true prevents re-triggering)
    const isInView = useInView(internalRef, {
      once: true,
      // margin: '0px 0px -50px 0px' // Trigger when element is 50px from entering viewport
      threshold: 0.1 // animate when 10% of the element is visible
    });

    // Calculate animation logic
    const shouldAnimate = (currentMode === 'post' && isNewComponent) || currentMode === 'initial';

    const shouldTriggerAnimation =
      shouldAnimate &&
      (currentMode === 'initial' || // Initial load: animate immediately
        currentMode === 'get' || // GET mode: animate immediately
        (currentMode === 'post' && isNewComponent && isInView)); // POST mode: animate when in view

    const chosenVariants = getGroupVariant(animationStyle)(
      index,
      isNewComponent && currentMode === 'post' // Only pass true for actual new components, not initial load
    );

    // Enhanced animation completion handler with timing
    const handleAnimationComplete = (definition) => {
      if (onAnimationComplete) {
        onAnimationComplete(definition);
      }
    };

    // Animation start handler
    const handleAnimationStart = () => {
      animationStartTime.current = performance.now();
    };

    // Set both refs - forward external ref and use internal ref for useInView
    useImperativeHandle(ref, () => internalRef.current);

    const isGet = currentMode === 'get';

    const animateProp = (() => {
      if (prefersReducedMotion) return 'visible';
      if (isGet) return 'visible';
      return shouldTriggerAnimation ? 'visible' : 'hidden';
    })();

    const initialProp = (() => {
      if (prefersReducedMotion) return 'visible';
      if (isGet) return 'visible';
      return shouldAnimate ? 'hidden' : 'visible';
    })();

    const variantsProp = (() => {
      if (prefersReducedMotion) return undefined;
      if (isGet) return undefined;
      return shouldAnimate ? chosenVariants : { hidden: {}, visible: {} };
    })();

    return (
      <MotionGroup
        ref={internalRef}
        animate={animateProp}
        centerGreeting={centerGreeting}
        custom={index}
        exit={isGet || prefersReducedMotion ? undefined : 'hidden'}
        fullWidth={fullWidth}
        initial={initialProp}
        variants={variantsProp}
        onAnimationComplete={handleAnimationComplete}
        onAnimationStart={handleAnimationStart}
      >
        {children}
      </MotionGroup>
    );
  }
);

AnimatedGroup.displayName = 'AnimatedGroup';

AnimatedGroup.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number,
  animationStyle: PropTypes.oneOf(['fade', 'slide', 'blur', 'grow']),
  currentMode: PropTypes.oneOf(['get', 'post', 'initial', null]),
  isNewComponent: PropTypes.bool,
  fullWidth: PropTypes.bool,
  centerGreeting: PropTypes.bool,
  onAnimationComplete: PropTypes.func
};

export default AnimatedGroup;
